import { logger } from '../../utils/logger';
import type { SnapshotComparisonResult } from '../../types/snapshotComparison';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

// ========================================
// TODO: DELETE - Demo migration mock data
// ========================================
// Demo migration ID
const DEMO_MIGRATION_ID = 'd08d06c5-5e7e-470d-b2ed-314e9bf6d79e';
const LIVE_MOCK_SNAPSHOT1 = 'd08d06c5-5e7e-470d-b2ed-314e9bf6d79e/source/2026_02_26_20_19_39';
const LIVE_MOCK_SNAPSHOT2 =
  'd08d06c5-5e7e-470d-b2ed-314e9bf6d79e/sourceOrgExport/2026_03_30_07_03_12';

const buildComparisonErrorMessage = async (response: Response): Promise<string> => {
  const fallback = `Failed to fetch snapshot comparison (${response.status})`;

  try {
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

    if (
      contentType.includes('application/json') ||
      contentType.includes('application/problem+json')
    ) {
      const body = (await response.json()) as Record<string, unknown>;
      const candidate = [body['error'], body['detail'], body['title'], body['message']].find(
        (v) => typeof v === 'string' && v.trim().length > 0
      ) as string | undefined;

      if (candidate) {
        return candidate;
      }
    }

    const text = await response.text();
    if (text.trim().length > 0) {
      return text;
    }
  } catch {
    // Fall through to fallback message.
  }

  return response.statusText?.trim().length ? `${fallback}: ${response.statusText}` : fallback;
};

/**
 * Fetch snapshot comparison results for a given migration from the backend API.
 * Calls GET /api/Migrations/{id}/compare and returns a diff of Genesys Cloud
 * resources that changed between two snapshots of the target organisation.
 *
 * @param bearerToken - Authentication token
 * @param migrationId - ID of the migration to compare
 * @param snapshot1 - Optional custom path to the first (before) snapshot. Defaults to {migrationId}/targetOrgSnapshot-preImport
 * @param snapshot2 - Optional custom path to the second (after) snapshot. Defaults to {migrationId}/targetOrgSnapshot-postImport
 */
export const fetchSnapshotComparison = async (
  bearerToken: string,
  migrationId: string,
  snapshot1?: string,
  snapshot2?: string
): Promise<SnapshotComparisonResult> => {
  const baseUrl = getEnv().VITE_EDCI_API_URL;

  // If this is the demo migration, use the live mock snapshot paths
  const isDemoMigration = migrationId === DEMO_MIGRATION_ID;

  // Use provided paths or construct default snapshot paths using migrationId
  // For demo migration, use the hardcoded live mock paths
  const snap1 = isDemoMigration
    ? LIVE_MOCK_SNAPSHOT1
    : (snapshot1 ?? `${migrationId}/targetOrgSnapshot-preImport`);
  const snap2 = isDemoMigration
    ? LIVE_MOCK_SNAPSHOT2
    : (snapshot2 ?? `${migrationId}/targetOrgSnapshot-postImport`);

  const params = new URLSearchParams();
  params.set('snapshot1', snap1);
  params.set('snapshot2', snap2);
  const query = `?${params.toString()}`;
  const url = `${baseUrl}/api/Migrations/${encodeURIComponent(migrationId)}/compare${query}`;

  logger.info('Fetching snapshot comparison', {
    migrationId,
    snapshot1: snap1,
    snapshot2: snap2,
    isDemo: isDemoMigration
  });

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: getV2AuthHeaders(bearerToken)
  });

  if (!response.ok) {
    const message = await buildComparisonErrorMessage(response);
    logger.error('Failed to fetch snapshot comparison', {
      migrationId,
      status: response.status,
      message
    });
    throw new Error(message);
  }

  const data = (await response.json()) as SnapshotComparisonResult;
  logger.info('Snapshot comparison fetched', {
    migrationId,
    resourceTypes: data.resourceTypes.length
  });
  return data;
};
